import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { ShieldCheck, ShieldAlert, Eye, FileDown, Archive, CheckCircle2, Layers, Truck } from "lucide-react";

// Founder narrative fields, ordered to mirror the customer-facing document.
// diagnosis + constraint_map are kept as internal working notes (not their own customer section).
const SECTIONS = [
  ["prepared_for", "Prepared for (cover)", "Owner or contact name shown on the cover. Defaults to the recorded owner.", "input"],
  ["short_version", "The Short Version", "Concise executive summary. What matters most, what to address first, the overall direction."],
  ["where_business_stands", "Where the Business Stands", "Context, owner objectives, strengths, systems worth preserving. Do not reproduce the assessment."],
  ["what_we_found", "What We Found (narrative)", "Your framing around the approved findings below. The approved records fill the detail."],
  ["core_constraint_explanation", "Core Constraint Explanation", "Optional. Plain-language reason you believe this is the central issue connecting the findings. Shows in the customer Core Constraint section (only when a Confirmed, included constraint is designated Core on the Diagnosis tab). If empty, the structured Core Constraint still renders."],
  ["fix_first", "What I Would Fix First (intro)", "Optional framing. The approved NOW interventions render as structured recommendations."],
  ["opportunity_plan", "Opportunities Worth Considering", "Useful but not urgent enough for the primary plan."],
  ["would_not_do", "What I Would Leave Alone", "Deliberate non-action. Explain why changing is not currently justified."],
  ["ninety_day", "90 Day Blueprint", "A practical sequence for roughly the next 90 days."],
  ["next_steps", "Next Steps", "Owner vs team vs outside specialist vs Long Range. Keep it objective, not a sales pitch."],
  ["diagnosis", "Internal: Diagnosis (working notes)", "Internal working notes. Not printed as its own customer section."],
  ["constraint_map", "Internal: Constraint Map (working notes)", "Internal working notes. Not printed as its own customer section."],
];

const GATE = [
  ["materially_useful", "Would this tell the owner something materially useful they did not already know?", ["Yes", "Partly", "No"]],
  ["explains_why", "Does it explain why the problems are happening rather than merely naming them?", ["Yes", "Partly", "No"]],
  ["order_of_operations", "Does it show the correct order of operations?", ["Yes", "Partly", "No"]],
  ["prevents_waste", "Does it help prevent wasted spending?", ["Yes", "Partly", "No"]],
  ["knows_next", "Does the owner leave knowing what to do next?", ["Yes", "Partly", "No"]],
  ["worth_297", "Would you personally feel comfortable charging $297 for this?", ["Yes", "No"]],
];

export default function BlueprintTab({ ws, bid, reload }) {
  const bp = ws.blueprint || {};
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [gateOpen, setGateOpen] = useState(false);
  const [gate, setGate] = useState({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [counts, setCounts] = useState(null);
  const [versions, setVersions] = useState([]);
  const [finalizing, setFinalizing] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [busyVid, setBusyVid] = useState(null);

  const custEmail = ws.business?.email || "";

  const loadVersions = () => api.get(`/businesses/${bid}/blueprint/versions`).then((r) => setVersions(r.data || [])).catch(() => {});

  useEffect(() => {
    setForm(Object.fromEntries(SECTIONS.map(([k]) => [k, bp[k] || ""])));
    setGate(bp.value_gate || {});
  }, [ws]);

  useEffect(() => {
    api.get(`/businesses/${bid}/blueprint/document`).then((r) => setCounts(r.data.counts)).catch(() => {});
    loadVersions();
  }, [bid, ws]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
    catch { return iso; }
  };

  const finalizeVersion = async () => {
    if (!window.confirm("Preserve the current Blueprint as a permanent version? This snapshots the exact document and PDF the customer will receive. Prior versions are kept.")) return;
    setFinalizing(true);
    try {
      await api.put(`/businesses/${bid}/blueprint`, form);
      const { data } = await api.post(`/businesses/${bid}/blueprint/versions`);
      toast.success(`Blueprint v${data.version} preserved`);
      await loadVersions();
      reload();
    } catch { toast.error("Could not finalize version"); }
    finally { setFinalizing(false); }
  };

  const downloadVersion = async (v) => {
    setBusyVid(v.id);
    try {
      const res = await api.get(`/blueprint/versions/${v.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = v.pdf_filename || `Blueprint v${v.version}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
    finally { setBusyVid(null); }
  };

  const markDelivered = async (v) => {
    if (!window.confirm(`Mark Blueprint v${v.version} as delivered? Do this only after you have actually sent it to the customer. The business will move to Blueprint Delivered.`)) return;
    setBusyVid(v.id);
    try {
      await api.post(`/blueprint/versions/${v.id}/deliver`);
      toast.success(`v${v.version} marked delivered`);
      await loadVersions();
      reload();
    } catch { toast.error("Could not mark delivered"); }
    finally { setBusyVid(null); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put(`/businesses/${bid}/blueprint`, form); toast.success("Blueprint saved"); reload(); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const openPreview = async () => {
    await api.put(`/businesses/${bid}/blueprint`, form);
    navigate(`/app/businesses/${bid}/blueprint/preview`);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await api.put(`/businesses/${bid}/blueprint`, form);
      const res = await api.get(`/businesses/${bid}/blueprint/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Blueprint.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Blueprint PDF generated");
      reload();
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  const runGate = async (setReady) => {
    try {
      await api.put(`/businesses/${bid}/blueprint`, form);
      const { data } = await api.post(`/businesses/${bid}/blueprint/gate`, { answers: gate, set_ready: setReady });
      if (setReady) { toast.success("Blueprint marked READY"); setGateOpen(false); }
      else toast[data.passed ? "success" : "info"](data.passed ? "Passes the $297 gate" : "Does not yet pass the $297 gate");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Blueprint cannot be marked READY. It did not pass the $297 gate.");
    }
  };

  const worthOk = gate.worth_297 === "Yes";

  return (
    <div className="max-w-3xl space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2.5 py-1 rounded-[3px] ${bp.status === "Ready" ? "bg-lr-olive/15 text-lr-olive" : "bg-lr-clay/15 text-lr-clay"}`}>
            {bp.status || "Draft"}
          </span>
          {bp.value_gate_passed && <span className="text-xs text-lr-olive flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> passed value gate</span>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={save} disabled={saving} variant="outline" className="rounded-[4px]" data-testid="save-blueprint">{saving ? "Saving…" : "Save"}</Button>
          <Button onClick={openPreview} variant="outline" className="rounded-[4px] border-lr-olive/40 text-lr-olive hover:bg-lr-olive/10" data-testid="preview-blueprint">
            <Eye className="h-4 w-4 mr-1.5" /> Preview
          </Button>
          <Button onClick={downloadPdf} disabled={downloading} variant="outline" className="rounded-[4px] border-lr-clay/40 text-lr-clay hover:bg-lr-clay/10" data-testid="download-blueprint-pdf">
            <FileDown className="h-4 w-4 mr-1.5" /> {downloading ? "Generating…" : "Download PDF"}
          </Button>
          <Button onClick={finalizeVersion} disabled={finalizing} variant="outline" className="rounded-[4px] border-lr-olive/40 text-lr-olive hover:bg-lr-olive/10" data-testid="finalize-blueprint-version">
            <Archive className="h-4 w-4 mr-1.5" /> {finalizing ? "Saving…" : "Finalize version"}
          </Button>
          <Button onClick={() => setGateOpen(true)} data-testid="open-gate" className="bg-lr-clay hover:bg-[#8d4f36] text-lr-cream rounded-[4px]">
            <ShieldAlert className="h-4 w-4 mr-1.5" /> Client Value Gate
          </Button>
        </div>
      </div>
      <p className="text-sm text-lr-leather">Founder must approve all content before delivery. Nothing appears in the customer Blueprint unless you have both marked the record final and toggled Include in Blueprint on the Diagnosis and Implementation tabs.</p>

      {counts && (
        <div className="lr-card p-4 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="blueprint-include-summary">
          <span className="text-lr-leather">Included in this Blueprint:</span>
          <span className="text-lr-charcoal">{counts.constraints} findings</span>
          <span className="text-lr-charcoal">{counts.fix_first} fix-first</span>
          <span className="text-lr-charcoal">{counts.interventions} plan items</span>
          <span className="text-lr-charcoal">{counts.assets} opportunities/assets</span>
          <span className="text-lr-charcoal">{counts.leave_alone} leave-alone</span>
        </div>
      )}

      {(() => {
        const latest = versions[0];
        return (
          <div className="lr-card p-5 space-y-3" data-testid="blueprint-delivery-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-serif text-lg text-lr-charcoal flex items-center gap-2"><Truck className="h-4 w-4 text-lr-olive" /> Delivery &amp; Versions</h3>
              {versions.length > 0 && (
                <Button variant="outline" size="sm" className="rounded-[4px]" onClick={() => setVersionsOpen(true)} data-testid="view-versions">
                  <Layers className="h-4 w-4 mr-1.5" /> View versions ({versions.length})
                </Button>
              )}
            </div>
            {latest ? (
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm" data-testid="latest-version-summary">
                <div><span className="text-lr-leather">Latest version</span><br /><span className="text-lr-charcoal font-medium" data-testid="latest-version-number">v{latest.version}</span> <span className="text-lr-leather text-xs">finalized {fmtDate(latest.created_at)}</span></div>
                <div><span className="text-lr-leather">Delivery</span><br />
                  {latest.delivered
                    ? <span className="text-lr-olive font-medium flex items-center gap-1" data-testid="latest-delivery-status"><CheckCircle2 className="h-4 w-4" /> Delivered {fmtDate(latest.delivered_at)}</span>
                    : <span className="text-lr-clay" data-testid="latest-delivery-status">Not yet delivered</span>}
                </div>
                <div><span className="text-lr-leather">Customer email</span><br /><span className="text-lr-charcoal" data-testid="latest-customer-email">{latest.customer_email || "Not recorded"}</span></div>
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" size="sm" className="rounded-[4px] border-lr-clay/40 text-lr-clay hover:bg-lr-clay/10" disabled={busyVid === latest.id} onClick={() => downloadVersion(latest)} data-testid="download-latest-version">
                    <FileDown className="h-4 w-4 mr-1.5" /> Download PDF
                  </Button>
                  {!latest.delivered && (
                    <Button size="sm" className="rounded-[4px] bg-lr-olive hover:bg-[#3d4230] text-lr-cream" disabled={busyVid === latest.id} onClick={() => markDelivered(latest)} data-testid="mark-delivered-latest">
                      <Truck className="h-4 w-4 mr-1.5" /> Mark Delivered
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-lr-leather">No preserved version yet. Use <span className="text-lr-olive">Finalize version</span> to snapshot the exact Blueprint (document + PDF) the customer will receive. You can then download or re-download it any time, even after editing the workspace.</p>
            )}
          </div>
        );
      })()}

      {SECTIONS.map(([k, label, helper, kind]) => (
        <div key={k} className="lr-card p-5" data-testid={`bp-${k}`}>
          <Label className="font-serif text-lg text-lr-charcoal">{label}</Label>
          {helper && <p className="text-xs text-lr-leather mt-0.5 mb-2">{helper}</p>}
          {kind === "input" ? (
            <Input className="bg-white/60" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`bp-field-${k}`} />
          ) : (
            <Textarea rows={4} className="mt-1 bg-white/60" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`bp-field-${k}`} />
          )}
        </div>
      ))}

      <Dialog open={gateOpen} onOpenChange={setGateOpen}>
        <DialogContent className="bg-lr-card max-h-[85vh] overflow-y-auto lr-scroll">
          <DialogHeader><DialogTitle className="font-serif">The $297 Client Value Gate</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            {GATE.map(([k, q, opts]) => (
              <div key={k}>
                <p className="text-sm text-lr-charcoal mb-2">{q}</p>
                <div className="flex gap-2">
                  {opts.map((o) => (
                    <button key={o} onClick={() => setGate({ ...gate, [k]: o })} data-testid={`gate-${k}-${o}`}
                      className={`px-3 py-1.5 rounded-[4px] border text-sm transition-colors ${gate[k] === o ? "bg-lr-olive text-lr-cream border-lr-olive" : "border-lr-leather/30 text-lr-charcoal hover:border-lr-olive"}`}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {!worthOk && gate.worth_297 === "No" && (
              <div className="text-sm text-[#8f4a49] bg-lr-rose/10 p-3 rounded-[4px]">
                This Blueprint cannot be marked READY. If you wouldn't charge $297 for it, improve it first.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => runGate(false)} className="rounded-[4px]" data-testid="gate-check">Check</Button>
            <Button onClick={() => runGate(true)} disabled={!worthOk} data-testid="gate-set-ready" className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">Mark READY</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="bg-lr-card max-h-[85vh] overflow-y-auto lr-scroll" data-testid="versions-dialog">
          <DialogHeader><DialogTitle className="font-serif">Preserved Blueprint versions</DialogTitle></DialogHeader>
          <p className="text-sm text-lr-leather">Each version is the exact document and PDF preserved at finalization. Editing the workspace never changes a preserved version.</p>
          <div className="space-y-3 py-2">
            {versions.map((v) => (
              <div key={v.id} className="lr-card p-4" data-testid={`version-row-${v.version}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="text-sm space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-lr-charcoal font-medium">v{v.version}</span>
                      {v.delivered
                        ? <span className="text-xs px-2 py-0.5 rounded-[3px] bg-lr-olive/15 text-lr-olive flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Delivered {fmtDate(v.delivered_at)}</span>
                        : <span className="text-xs px-2 py-0.5 rounded-[3px] bg-lr-clay/15 text-lr-clay">Not delivered</span>}
                    </div>
                    <div className="text-lr-leather text-xs">Finalized {fmtDate(v.created_at)}</div>
                    <div className="text-lr-leather text-xs">Prepared for {v.prepared_for || v.business_name} · {v.customer_email || "no email recorded"}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-[4px] border-lr-clay/40 text-lr-clay hover:bg-lr-clay/10" disabled={busyVid === v.id} onClick={() => downloadVersion(v)} data-testid={`download-version-${v.version}`}>
                      <FileDown className="h-4 w-4 mr-1.5" /> Download
                    </Button>
                    {!v.delivered && (
                      <Button size="sm" className="rounded-[4px] bg-lr-olive hover:bg-[#3d4230] text-lr-cream" disabled={busyVid === v.id} onClick={() => markDelivered(v)} data-testid={`deliver-version-${v.version}`}>
                        Mark Delivered
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
